import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import useRealtimeNotifications from '../../hooks/useRealtimeNotifications';
import EmptyState from '../../components/ui/EmptyState';
import {
  trackNotificationCenterOpen,
  trackNotificationCategoryFilter,
  trackNotificationRead,
  trackNotificationDeleted,
  trackNotificationBulkAction,
} from '../../services/monEspaceAnalyticsService';

// ─── Category definitions ────────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: 'all',
    label: 'Tout',
    icon: 'LayoutGrid',
    types: null,
    color: 'brand-earth',
    accentBg: 'bg-brand-earth/10',
    accentText: 'text-brand-earth',
    accentBorder: 'border-brand-earth',
  },
  {
    id: 'inquiries',
    label: 'Demandes',
    icon: 'MessageSquare',
    types: ['property_inquiry', 'general'],
    color: 'amber',
    accentBg: 'bg-amber-50',
    accentText: 'text-amber-700',
    accentBorder: 'border-amber-400',
  },
  {
    id: 'viewings',
    label: 'Visites',
    icon: 'CalendarCheck',
    types: ['appointment_confirmation', 'appointment', 'notary_appointment', 'booking_update'],
    color: 'blue',
    accentBg: 'bg-blue-50',
    accentText: 'text-blue-700',
    accentBorder: 'border-blue-400',
  },
  {
    id: 'saved',
    label: 'Propriétés sauvegardées',
    icon: 'Bookmark',
    types: ['property_update', 'document_ready'],
    color: 'green',
    accentBg: 'bg-green-50',
    accentText: 'text-green-700',
    accentBorder: 'border-green-400',
  },
  {
    id: 'alerts',
    label: 'Alertes déclenchées',
    icon: 'BellRing',
    types: ['property_alert'],
    color: 'purple',
    accentBg: 'bg-purple-50',
    accentText: 'text-purple-700',
    accentBorder: 'border-purple-400',
  },
  {
    id: 'security',
    label: 'Sécurité',
    icon: 'ShieldAlert',
    types: ['security_alert'],
    color: 'red',
    accentBg: 'bg-red-50',
    accentText: 'text-red-700',
    accentBorder: 'border-red-400',
  },
];

const TYPE_META = {
  appointment_confirmation: { label: 'Confirmation RDV', icon: 'CalendarCheck', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', leftBorder: 'border-l-blue-400' },
  appointment: { label: 'Rendez-vous', icon: 'Calendar', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', leftBorder: 'border-l-blue-400' },
  notary_appointment: { label: 'RDV Notaire', icon: 'Scale', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', leftBorder: 'border-l-purple-400' },
  booking_update: { label: 'Réservation', icon: 'BookOpen', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', leftBorder: 'border-l-blue-400' },
  property_inquiry: { label: 'Demande', icon: 'Home', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', leftBorder: 'border-l-amber-400' },
  property_update: { label: 'Mise à jour', icon: 'RefreshCw', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', leftBorder: 'border-l-green-400' },
  property_alert: { label: 'Alerte propriété', icon: 'BellRing', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', leftBorder: 'border-l-purple-400' },
  document_ready: { label: 'Document prêt', icon: 'FileText', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', leftBorder: 'border-l-indigo-400' },
  security_alert: { label: 'Alerte sécurité', icon: 'ShieldAlert', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', leftBorder: 'border-l-red-500' },
  general: { label: 'Général', icon: 'Bell', bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', leftBorder: 'border-l-gray-300' },
};

const getMeta = (type) => TYPE_META?.[type] || TYPE_META?.general;

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'À l\'instant';
  if (mins < 60) return `Il y a ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Il y a ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `Il y a ${days}j`;
  return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

// ─── Notification Card ───────────────────────────────────────────────────────

const NotificationCard = ({ notification, isSelected, onSelect, onMarkRead, onDelete }) => {
  const meta = getMeta(notification?.type);
  const isUnread = !notification?.is_read;

  return (
    <div
      className={`group relative bg-white rounded-2xl border border-l-4 ${meta?.leftBorder} transition-all duration-200 hover:shadow-ios-medium ${
        isUnread ? 'border-border/80 shadow-ios-light ring-1 ring-brand-gold/15' : 'border-border/40 shadow-ios-light/50'
      } ${isSelected ? 'ring-2 ring-brand-earth/30' : ''}`}
    >
      {/* Selection checkbox */}
      <div className="absolute top-4 left-4 z-10">
        <button
          onClick={() => onSelect(notification?.id)}
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-150 ${
            isSelected
              ? 'bg-brand-earth border-brand-earth' :'border-border bg-white opacity-0 group-hover:opacity-100'
          }`}
        >
          {isSelected && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
        </button>
      </div>

      <div className="p-4 pl-12">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${meta?.bg} ${meta?.border} border`}>
            <Icon name={meta?.icon} size={18} className={meta?.text} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                  {isUnread && (
                    <span className="w-2 h-2 rounded-full bg-brand-gold flex-shrink-0" />
                  )}
                  <h3 className={`text-ios-callout font-system leading-snug truncate ${isUnread ? 'font-body-semibold text-text-primary' : 'font-body-medium text-text-secondary'}`}>
                    {notification?.title || 'Notification'}
                  </h3>
                </div>
                <p className={`text-ios-footnote leading-relaxed line-clamp-2 ${isUnread ? 'text-text-primary' : 'text-text-secondary'}`}>
                  {notification?.message || '—'}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  <span className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded-lg text-ios-caption1 font-body-medium ${meta?.bg} ${meta?.text} ${meta?.border} border`}>
                    <Icon name={meta?.icon} size={10} />
                    <span>{meta?.label}</span>
                  </span>
                  <span className="flex items-center space-x-1 text-ios-caption1 text-text-secondary">
                    <Icon name="Clock" size={11} />
                    <span>{formatRelativeTime(notification?.created_at)}</span>
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                {isUnread && (
                  <button
                    onClick={() => onMarkRead(notification?.id)}
                    title="Marquer comme lu"
                    className="w-8 h-8 rounded-xl bg-surface hover:bg-brand-earth/10 flex items-center justify-center transition-colors"
                  >
                    <Icon name="Check" size={14} className="text-text-secondary hover:text-brand-earth" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(notification?.id)}
                  title="Supprimer"
                  className="w-8 h-8 rounded-xl bg-surface hover:bg-red-50 flex items-center justify-center transition-colors"
                >
                  <Icon name="Trash2" size={14} className="text-text-secondary hover:text-red-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Screen ─────────────────────────────────────────────────────────────

const NotificationsCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { unreadCount, refreshCount } = useRealtimeNotifications(user);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [showBulkBar, setShowBulkBar] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const prevUnreadRef = useRef(0);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('recipient_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(200);

      if (error) throw error;

      const all = data || [];
      setNotifications(all);

      // Compute per-category unread counts
      const counts = {};
      CATEGORIES?.forEach((cat) => {
        if (cat?.id === 'all') {
          counts['all'] = all?.filter((n) => !n?.is_read)?.length;
        } else {
          counts[cat.id] = all?.filter(
            (n) => !n?.is_read && cat?.types?.includes(n?.type)
          )?.length;
        }
      });
      setCategoryCounts(counts);
      setLiveCount(counts?.['all'] || 0);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Track page open once notifications are loaded
  useEffect(() => {
    if (!loading && notifications?.length >= 0) {
      trackNotificationCenterOpen(liveCount);
    }
  }, [loading]);

  // Refresh when realtime unread count changes
  useEffect(() => {
    if (unreadCount !== prevUnreadRef?.current) {
      prevUnreadRef.current = unreadCount;
      fetchNotifications();
    }
  }, [unreadCount, fetchNotifications]);

  // Show/hide bulk action bar
  useEffect(() => {
    setShowBulkBar(selectedIds?.length > 0);
  }, [selectedIds]);

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filteredNotifications = notifications?.filter((n) => {
    if (activeCategory === 'all') return true;
    const cat = CATEGORIES?.find((c) => c?.id === activeCategory);
    return cat?.types?.includes(n?.type);
  });

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleMarkRead = async (id) => {
    try {
      await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', id);
      const n = notifications?.find(x => x?.id === id);
      trackNotificationRead(n?.type);
      setNotifications((prev) =>
        prev?.map((n) => (n?.id === id ? { ...n, is_read: true } : n))
      );
      refreshCount?.();
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await supabase?.from('notifications')?.delete()?.eq('id', id);
      const n = notifications?.find(x => x?.id === id);
      trackNotificationDeleted(n?.type);
      setNotifications((prev) => prev?.filter((n) => n?.id !== id));
      setSelectedIds((prev) => prev?.filter((sid) => sid !== id));
      refreshCount?.();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds((prev) =>
      prev?.includes(id) ? prev?.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    const visibleIds = filteredNotifications?.map((n) => n?.id);
    const allSelected = visibleIds?.every((id) => selectedIds?.includes(id));
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(visibleIds);
    }
  };

  const handleBulkMarkRead = async () => {
    if (!selectedIds?.length) return;
    setBulkLoading(true);
    try {
      await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.in('id', selectedIds);
      trackNotificationBulkAction('mark_read', selectedIds?.length);
      setNotifications((prev) =>
        prev?.map((n) => (selectedIds?.includes(n?.id) ? { ...n, is_read: true } : n))
      );
      setSelectedIds([]);
      refreshCount?.();
      fetchNotifications();
      showSuccess(`${selectedIds?.length} notification(s) marquée(s) comme lue(s)`);
    } catch (err) {
      console.error(err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds?.length) return;
    if (!window.confirm(`Supprimer ${selectedIds?.length} notification(s) ?`)) return;
    setBulkLoading(true);
    try {
      await supabase?.from('notifications')?.delete()?.in('id', selectedIds);
      trackNotificationBulkAction('delete', selectedIds?.length);
      setNotifications((prev) => prev?.filter((n) => !selectedIds?.includes(n?.id)));
      setSelectedIds([]);
      refreshCount?.();
      showSuccess(`${selectedIds?.length} notification(s) supprimée(s)`);
    } catch (err) {
      console.error(err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    setBulkLoading(true);
    try {
      let query = supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('is_read', false);
      if (user?.id) query = query?.eq('recipient_id', user?.id);
      await query;
      setNotifications((prev) => prev?.map((n) => ({ ...n, is_read: true })));
      setSelectedIds([]);
      refreshCount?.();
      fetchNotifications();
      showSuccess('Toutes les notifications marquées comme lues');
    } catch (err) {
      console.error(err);
    } finally {
      setBulkLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const visibleIds = filteredNotifications?.map((n) => n?.id);
  const allVisibleSelected =
    visibleIds?.length > 0 && visibleIds?.every((id) => selectedIds?.includes(id));

  return (
    <>
      <Helmet>
        <title>Centre de Notifications – Kapital States</title>
        <meta
          name="description"
          content="Suivez en temps réel vos alertes : demandes de propriétés, visites, biens sauvegardés et alertes déclenchées."
        />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* ── Breadcrumb + Header ── */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-3">
              <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
              <Icon name="ChevronRight" size={13} />
              <span className="text-text-primary font-body-medium">Centre de Notifications</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-brand-gold/20 to-brand-earth/20 rounded-2xl flex items-center justify-center">
                    <Icon name="Bell" size={24} className="text-brand-earth" />
                  </div>
                  {liveCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none shadow-sm">
                      {liveCount > 99 ? '99+' : liveCount}
                    </span>
                  )}
                </div>
                <div>
                  <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
                    Centre de Notifications
                  </h1>
                  <p className="text-ios-footnote text-text-secondary mt-0.5">
                    {liveCount > 0
                      ? `${liveCount} non lue${liveCount > 1 ? 's' : ''} · Mises à jour en temps réel`
                      : 'Tout est à jour · Mises à jour en temps réel'}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2 self-start sm:self-auto">
                {liveCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    disabled={bulkLoading}
                    className="flex items-center space-x-2 px-4 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
                  >
                    <Icon name="CheckCheck" size={15} color="white" />
                    <span className="whitespace-nowrap">Tout marquer lu</span>
                  </button>
                )}
                <Link
                  to="/notification-preferences"
                  className="flex items-center space-x-2 px-4 py-2.5 bg-white border border-border text-text-primary rounded-xl text-ios-callout font-system font-body-medium hover:border-brand-earth/40 hover:text-brand-earth transition-all duration-200 shadow-ios-light"
                >
                  <Icon name="Settings2" size={15} />
                  <span className="hidden sm:inline whitespace-nowrap">Préférences</span>
                </Link>
              </div>
            </div>
          </div>

          {/* ── Success Toast ── */}
          {successMsg && (
            <div className="mb-4 flex items-center space-x-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-ios-callout text-green-700 font-body-medium animate-slide-up">
              <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* ── Category Tabs ── */}
          <div className="mb-5 overflow-x-auto pb-1 -mx-1 px-1">
            <div className="flex items-center gap-2 min-w-max">
              {CATEGORIES?.map((cat) => {
                const isActive = activeCategory === cat?.id;
                const count = categoryCounts?.[cat?.id] || 0;
                return (
                  <button
                    key={cat?.id}
                    onClick={() => {
                      setActiveCategory(cat?.id);
                      setSelectedIds([]);
                      trackNotificationCategoryFilter(cat?.id);
                    }}
                    className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl text-ios-callout font-system font-body-medium transition-all duration-200 whitespace-nowrap border ${
                      isActive
                        ? `${cat?.accentBg} ${cat?.accentText} ${cat?.accentBorder} shadow-ios-light`
                        : 'bg-white border-border text-text-secondary hover:border-brand-earth/30 hover:text-text-primary'
                    }`}
                  >
                    <Icon name={cat?.icon} size={15} strokeWidth={2} />
                    <span>{cat?.label}</span>
                    {count > 0 && (
                      <span className={`min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center leading-none ${
                        isActive ? 'bg-white/70 text-current' : 'bg-red-500 text-white'
                      }`}>
                        {count > 99 ? '99+' : count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Bulk Action Bar ── */}
          {showBulkBar && (
            <div className="mb-4 flex items-center justify-between px-4 py-3 bg-brand-earth/5 border border-brand-earth/20 rounded-2xl animate-slide-up">
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center space-x-2 text-ios-callout font-body-medium text-brand-earth hover:text-brand-earth/80 transition-colors"
                >
                  <div className={`w-5 h-5 rounded-md border-2 border-brand-earth flex items-center justify-center ${allVisibleSelected ? 'bg-brand-earth' : 'bg-white'}`}>
                    {allVisibleSelected && <Icon name="Check" size={11} color="white" strokeWidth={3} />}
                  </div>
                  <span>{selectedIds?.length} sélectionnée{selectedIds?.length > 1 ? 's' : ''}</span>
                </button>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkMarkRead}
                  disabled={bulkLoading}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-white border border-border rounded-xl text-ios-footnote font-body-medium text-text-primary hover:border-brand-earth/40 hover:text-brand-earth disabled:opacity-60 transition-all duration-150"
                >
                  {bulkLoading ? (
                    <div className="w-3.5 h-3.5 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="CheckCheck" size={14} />
                  )}
                  <span>Marquer lues</span>
                </button>
                <button
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="flex items-center space-x-1.5 px-3 py-2 bg-white border border-border rounded-xl text-ios-footnote font-body-medium text-text-primary hover:border-red-300 hover:text-red-500 disabled:opacity-60 transition-all duration-150"
                >
                  <Icon name="Trash2" size={14} />
                  <span>Supprimer</span>
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  className="w-8 h-8 rounded-xl bg-white border border-border flex items-center justify-center hover:border-border/80 transition-colors"
                >
                  <Icon name="X" size={14} className="text-text-secondary" />
                </button>
              </div>
            </div>
          )}

          {/* ── Select All Row (when not in bulk mode) ── */}
          {!showBulkBar && filteredNotifications?.length > 0 && !loading && (
            <div className="flex items-center justify-between mb-3 px-1">
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-2 text-ios-footnote text-text-secondary hover:text-text-primary transition-colors"
              >
                <div className={`w-4 h-4 rounded border-2 border-border flex items-center justify-center ${allVisibleSelected ? 'bg-brand-earth border-brand-earth' : 'bg-white'}`}>
                  {allVisibleSelected && <Icon name="Check" size={9} color="white" strokeWidth={3} />}
                </div>
                <span>Tout sélectionner</span>
              </button>
              <span className="text-ios-caption1 text-text-secondary">
                {filteredNotifications?.length} notification{filteredNotifications?.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* ── Notifications List ── */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4]?.map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-border/40 p-4 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-surface flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-surface rounded-lg w-2/3" />
                      <div className="h-3 bg-surface rounded-lg w-full" />
                      <div className="h-3 bg-surface rounded-lg w-1/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredNotifications?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-border/40 shadow-ios-light">
              <EmptyState
                iconName="BellOff"
                title="Aucune notification"
                description={
                  activeCategory === 'all' ? "Vous n'avez pas encore de notifications. Elles apparaîtront ici en temps réel."
                    : `Aucune notification dans la catégorie "${CATEGORIES?.find((c) => c?.id === activeCategory)?.label}".`
                }
                ctaLabel={activeCategory !== 'all' ? 'Voir toutes les notifications' : undefined}
                ctaIcon={activeCategory !== 'all' ? 'LayoutGrid' : undefined}
                ctaVariant="outline"
                onCta={activeCategory !== 'all' ? () => setActiveCategory('all') : undefined}
                size="lg"
              />
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredNotifications?.map((notification) => (
                <NotificationCard
                  key={notification?.id}
                  notification={notification}
                  isSelected={selectedIds?.includes(notification?.id)}
                  onSelect={handleSelect}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* ── Footer ── */}
          {!loading && filteredNotifications?.length > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3 pt-6 border-t border-border/50">
              <p className="text-ios-footnote text-text-secondary">
                <span className="font-body-medium text-text-primary">{filteredNotifications?.length}</span> notification{filteredNotifications?.length !== 1 ? 's' : ''} affichée{filteredNotifications?.length !== 1 ? 's' : ''}
              </p>
              <Link
                to="/notification-history"
                className="flex items-center space-x-2 text-ios-callout text-brand-earth font-body-medium hover:underline"
              >
                <Icon name="History" size={15} />
                <span>Voir l'historique complet</span>
              </Link>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default NotificationsCenter;
